import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { MOVE_TRANSACTIONS } from '../graphql/mutations/transactions';
import TokenSelector from './allocation/TokenSelector';

const MoveTransactionModal = ({ isOpen, onClose, transactions, portfolioId, sourceTokenId, onMoved }) => {
  const [selectedToken, setSelectedToken] = useState(null);
  const [activeSelectorIndex, setActiveSelectorIndex] = useState(null);
  const [error, setError] = useState(null);
  const [violations, setViolations] = useState([]);
  const submitRef = useRef(null);

  const [moveTransactions, { loading }] = useMutation(MOVE_TRANSACTIONS);

  const count = transactions?.length || 0;

  useEffect(() => {
    if (isOpen) {
      setSelectedToken(null);
      setError(null);
      setViolations([]);
      setActiveSelectorIndex(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      if (activeSelectorIndex !== null) {
        setActiveSelectorIndex(null);
      } else {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeSelectorIndex, onClose]);

  const isSameToken = (token) => {
    if (!token || !sourceTokenId) return false;
    if (token.tokenId && token.tokenId === sourceTokenId) return true;
    if (token.coingeckoApiId && sourceTokenId === `cg:${token.coingeckoApiId}`) return true;
    return false;
  };

  const handleTokenSelected = (token) => {
    setSelectedToken(token);
    setViolations([]);
    setActiveSelectorIndex(null);
    if (isSameToken(token)) {
      setError('Already on this token');
    } else {
      setError(null);
      setTimeout(() => submitRef.current?.focus(), 0);
    }
  };

  const toggleSelector = (index) => {
    setActiveSelectorIndex((prev) => (prev === index ? null : index));
  };

  const hasValidToken = (selectedToken?.tokenId || selectedToken?.coingeckoApiId) && !isSameToken(selectedToken);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedToken || !hasValidToken || count === 0) return;

    setError(null);
    setViolations([]);

    try {
      const targetTokenId = selectedToken.tokenId || `cg:${selectedToken.coingeckoApiId}`;
      const transactionIds = transactions.map((tx) => tx.id);

      const { data } = await moveTransactions({
        variables: { portfolioId, transactionIds, targetTokenId },
      });

      const result = data?.moveTransactions;
      if (!result?.success) {
        if (result?.violations?.length > 0) {
          setViolations(result.violations);
          setError(`Move blocked: ${result.violations.length} validation issue${result.violations.length > 1 ? 's' : ''}`);
        } else {
          setError(result?.error || 'Move failed');
        }
        return;
      }

      onMoved?.(result, selectedToken);
      onClose();
    } catch (err) {
      setError(err.message || 'Move failed');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Move {count} transaction{count === 1 ? '' : 's'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
          >
            <span className="sr-only">Close</span>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Move to token
            </label>
            <TokenSelector
              selectedTokenId={selectedToken?.tokenId || selectedToken?.id || ''}
              selectedToken={selectedToken}
              selectedCoingeckoApiId={selectedToken?.coingeckoApiId || null}
              onSelectToken={handleTokenSelected}
              index={0}
              isActive={activeSelectorIndex === 0}
              toggleActive={toggleSelector}
              existingTokenIds={[]}
              existingCoingeckoApiIds={[]}
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-3">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {violations.length > 0 && (
            <div className="max-h-40 overflow-y-auto rounded-md bg-red-50 dark:bg-red-900/20 p-3 space-y-2">
              {violations.map((v, i) => (
                <p key={i} className="text-xs text-red-600 dark:text-red-400">
                  {v.message}
                </p>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              ref={submitRef}
              type="submit"
              className="inline-flex items-center px-4 py-2 rounded-md border border-transparent text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
              disabled={loading || !hasValidToken}
            >
              {loading ? 'Moving...' : 'Move'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MoveTransactionModal;
